/**
 * ChatComposer Component
 * ======================
 * 
 * Message input area: textarea, send button, voice button.
 * 
 * Handles:
 * - Auto-grow textarea
 * - Send on Enter
 * - Voice recording button (identity-gated)
 * 
 * Props-driven, all handlers from parent.
 */
import React from 'react';
import { Send } from 'lucide-react';

export function ChatComposer({
  // Message state
  newMessage,
  onMessageChange,
  onSendMessage,
  sending,
  
  // Voice recording
  canSendVoice,
  isRecording,
  recordingDuration,
  audioError,
  onVoiceClick,
  onCancelRecording,
  
  // Typing indicator
  isPartnerTyping,
  
  // Inner work nudge
  canShowInnerNudge,
  innerNudgeContent,
  
  // Assist
  assistContent,
  
  // Disabled state
  disabled = false,
}) {
  return (
    <div className="border-t border-gray-200 px-1 sm:px-4 py-3 sm:py-4 mobile-safe-bottom">
      {/* Typing indicator */}
      {isPartnerTyping && (
        <div className="px-4 pb-1 text-[11px] italic text-slate-400">
          They&apos;re typing…
        </div>
      )}
      
      {/* Recording status */}
      {isRecording && (
        <div className="px-2 py-2 mb-2 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
            <span className="text-sm text-red-700 font-medium">
              Recording {Math.floor(recordingDuration / 1000)}s
            </span>
          </div>
          <button
            type="button"
            onClick={onCancelRecording}
            className="text-xs text-red-600 hover:text-red-800 font-medium"
          >
            Cancel
          </button>
        </div>
      )}

      {audioError && (
        <div className="px-2 py-2 mb-2 bg-yellow-50 border border-yellow-200 rounded-lg text-xs text-yellow-700">
          {audioError}
        </div>
      )}

      {/* Inner Work Nudge Banner */}
      {canShowInnerNudge && innerNudgeContent}

      {/* Composer Assist */}
      {assistContent}

      {/* Input Row */}
      <form 
        onSubmit={(e) => {
          e.preventDefault();
          if (newMessage.trim() && !sending && !isRecording) {
            onSendMessage(e);
          }
        }}
        className="flex items-end gap-1 sm:gap-2"
        data-testid="chat-composer-form"
      >
        {/* Textarea Container */}
        <div className="relative flex-1 min-w-0">
          <textarea
            value={newMessage}
            onChange={(e) => onMessageChange(e.target.value)}
            placeholder="Type a message..."
            className="w-full resize-none rounded-2xl border border-gray-200 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base focus:outline-none focus:border-rose-400 focus:ring-1 focus:ring-rose-400 box-border max-w-full overflow-hidden"
            disabled={sending || isRecording || disabled}
            data-testid="message-input"
            rows={1}
            onInput={(e) => {
              // auto-grow
              e.currentTarget.style.height = "auto";
              e.currentTarget.style.height = `${Math.min(e.currentTarget.scrollHeight, 140)}px`;
            }}
            onKeyDown={(e) => {
              // Submit on Enter (without Shift)
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (newMessage.trim() && !sending && !isRecording) {
                  onSendMessage(e);
                }
              }
            }}
          />
        </div>

        {/* Right-side buttons */}
        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          {/* Voice button */}
          <VoiceButton
            canSendVoice={canSendVoice}
            isRecording={isRecording}
            onClick={onVoiceClick}
          />
          
          {/* Send button */}
          <button
            type="submit"
            disabled={sending || !newMessage.trim() || isRecording || disabled}
            className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition ${
              sending || !newMessage.trim() || isRecording || disabled
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-rose-500 hover:bg-rose-600"
            }`}
            data-testid="send-button"
          >
            <Send className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * VoiceButton - Mic button for voice messages
 */
function VoiceButton({ canSendVoice, isRecording, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-disabled={!canSendVoice}
      className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition ${
        !canSendVoice
          ? "bg-gray-300 text-gray-400 opacity-50 cursor-not-allowed"
          : isRecording
          ? "bg-red-600 text-white animate-pulse"
          : "bg-gray-200 hover:bg-gray-300 text-gray-700"
      }`}
      title={
        !canSendVoice
          ? "Verify your identity to send voice notes"
          : isRecording
          ? "Tap to stop & send"
          : "Tap to record voice message"
      }
      data-testid="voice-button"
    >
      {isRecording ? (
        <span className="text-[10px] font-bold">REC</span>
      ) : (
        <MicIcon />
      )}
    </button>
  );
}

/**
 * MicIcon - SVG mic icon
 */
function MicIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="w-4 h-4 sm:w-5 sm:h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
      />
    </svg>
  );
}

export default ChatComposer;
